import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtTL, fmtDate } from "@/lib/format";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, HandCoins } from "lucide-react";

export default function Payments() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState({ banks: [], cash: [] });
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", amount: "", method: "nakit", account_id: "", account_type: "cash", note: "" });

  const load = async () => setItems((await api.get("/payments")).data);
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
    if (params.get("new")) {
      setOpen(true);
      const c = params.get("customer");
      if (c) setForm((f) => ({ ...f, customer_id: c }));
    }
  }, []);

  const allAccounts = [...accounts.banks.map((b) => ({ ...b, type: "bank" })), ...accounts.cash.map((c) => ({ ...c, type: "cash" }))];

  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.customer_id) return toast.error("Müşteri seçin");
    if (!Number(form.amount) || Number(form.amount) <= 0) return toast.error("Geçerli tutar girin");
    if (!form.account_id) return toast.error("Hesap seçin");
    setSaving(true);
    try {
      await api.post("/payments", { ...form, amount: Number(form.amount) });
      toast.success("Ödeme başarıyla kaydedildi");
      setOpen(false);
      setForm({ customer_id: "", amount: "", method: "nakit", account_id: "", account_type: "cash", note: "" });
      load();
    } catch (e) { if (e.response && e.response.status < 500) toast.error(e.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };

  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/payments/${id}`); toast.success("Silindi"); load(); };

  const totalToday = items.filter((p) => new Date(p.date).toDateString() === new Date().toDateString()).reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Tahsilatlar</h1>
          <p className="text-sm text-muted-foreground">Bugün: <span className="num font-semibold text-emerald-500">{fmtTL(totalToday)}</span></p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-payment-btn"><HandCoins size={16} className="mr-1" /> Ödeme Al</Button></DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle>Ödeme Al</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Müşteri *</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="payment-customer-select"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent className="bg-popover">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · Borç: {fmtTL(c.balance)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tutar (₺) *</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Yöntem</Label>
                  <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover"><SelectItem value="nakit">Nakit</SelectItem><SelectItem value="havale">Havale/EFT</SelectItem><SelectItem value="kart">Kart</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Hesap *</Label>
                  <Select value={form.account_id} onValueChange={(v) => {
                    const a = allAccounts.find((x) => x.id === v);
                    setForm({ ...form, account_id: v, account_type: a?.type || "cash" });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent className="bg-popover">{allAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Not</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
              <Button type="submit" className="w-full" disabled={saving} data-testid="payment-submit-btn">{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tarih</TableHead><TableHead>Müşteri</TableHead><TableHead>Yöntem</TableHead>
            <TableHead className="text-right">Tutar</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{fmtDate(p.date)}</TableCell>
                <TableCell className="font-medium">{p.customer_name}</TableCell>
                <TableCell><span className="text-xs px-2 py-0.5 rounded bg-muted uppercase">{p.method}</span></TableCell>
                <TableCell className="text-right num font-semibold text-emerald-500">{fmtTL(p.amount)}</TableCell>
                <TableCell>{isAdmin && <Button variant="ghost" size="icon" onClick={() => del(p.id)} data-testid={`payment-delete-${p.id}`}><Trash2 size={14} /></Button>}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Henüz tahsilat yok</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
